/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { ColumnInputProps } from '../../InputProps';
import { ValueList } from '@g17eco/types/valueList';
import { FormGroup, InputGroup } from 'reactstrap';
import { loadValueListById, valueListSelectors } from '@g17eco/slices/valueListSlice';
import { initCustomOptions, mergeCustomOptions } from '../../selectUtils';
import { useAppDispatch, useAppSelector } from '@reducers/index';
import { getSingleRowTableAddon } from '../tableUtils';
import { Option as SelectFactoryOption } from '@g17eco/molecules/select/SelectFactory';
import { SelectComponent } from '../../SelectComponent';
import { MergedValueListOption } from '@g17eco/types/initiativeUniversalTracker';
import { InputColumn } from '@g17eco/types/questionInterfaces';
import { normalizeText } from '@utils/string-format';

const getValues = (currentValue: InputColumn['value']) => {
  if (Array.isArray(currentValue)) {
    return currentValue;
  }
  return currentValue !== undefined && currentValue !== null ? [String(currentValue)] : [];
};
const getOptions = (params: {
  options: MergedValueListOption[] | undefined;
  isDisabled?: boolean;
  currentValue?: InputColumn['value'];
  subsidiaryOptions?: MergedValueListOption[];
}) => {
  const { options, isDisabled, currentValue, subsidiaryOptions } = params;
  const currentValues = getValues(currentValue);

  const mergedOptions = mergeCustomOptions(options ?? [], subsidiaryOptions);

  return mergedOptions.reduce((acc, o) => {
    const isCurrentValue = currentValues.includes(o.code);

    if (!o.hidden || isCurrentValue) {
      acc.push({ value: o.code, label: o.name, searchString: `${o.code} ${o.name}`, isDisabled });
    }
    return acc;
  }, [] as SelectFactoryOption<string>[]);
};

const renderInputGroup = (label: any, content: any) => (
  <FormGroup>
    {label}
    <InputGroup className='mb-3'>{content}</InputGroup>
  </FormGroup>
);

const SelectInput = (props: ColumnInputProps) => {
  const {
    isDisabled,
    updateColumn,
    column,
    label,
    inputColumn,
    placeholder,
    isReadOnlyOptions,
    onSaveCustomOption,
    subsidiaryUtr,
  } = props;

  const isMultipleChoice = props.multi === true;
  const allowCustomOptions = !!column.validation?.allowCustomOptions;

  const { beforeAddon, afterAddon, belowInputAddon } = getSingleRowTableAddon({ ...props, columnCode: column.code });

  const handleChange = (newValue?: string[] | string) => {
    updateColumn({ code: column.code, value: newValue });
  };

  const dispatch = useAppDispatch();
  const valueList: ValueList | undefined = useAppSelector((state) =>
    valueListSelectors.selectById(state, column.listId || ''),
  );

  const [options, setOptions] = useState<SelectFactoryOption<string>[]>([]);

  const subsidiaryOptions = subsidiaryUtr?.valueListConfig?.table?.columns.find((c) => c.code === column.code)?.options;

  React.useEffect(() => {
    // Options from column might contain overridden options, hence we need to use column options if it exists
    let baseOptions: SelectFactoryOption<string>[] = [];

    if (column.options) {
      baseOptions = getOptions({
        options: column.options,
        isDisabled: isReadOnlyOptions,
        currentValue: inputColumn.value,
        subsidiaryOptions,
      });
    } else if (valueList?.options) {
      baseOptions = getOptions({
        options: valueList.options,
        isDisabled: isReadOnlyOptions,
        currentValue: inputColumn.value,
        subsidiaryOptions,
      });
    } else if (column.listId) {
      if (valueList?.loading || valueList?.loaded) {
        return;
      }
      dispatch(loadValueListById(column.listId));
      return;
    }

    // deals with adding custom option from column value on edit
    if (!inputColumn.value || !baseOptions.length || (Array.isArray(inputColumn.value) && !inputColumn.value.length)) {
      setOptions(baseOptions);
      return;
    }

    const selectOptionValues = baseOptions.map((op) => normalizeText(String(op.value)));
    const isSelectedValueExisting = Array.isArray(inputColumn.value)
      ? inputColumn.value.every((value) => selectOptionValues.includes(normalizeText(String(value))))
      : selectOptionValues.includes(normalizeText(String(inputColumn.value)));

    // all selected values (including custom values) are already in options => use base options
    if (isSelectedValueExisting) {
      setOptions(baseOptions);
      return;
    }

    // add custom options for values not found in base options
    const finalOptions = initCustomOptions(
      Array.isArray(inputColumn.value) ? inputColumn.value.map(String) : [String(inputColumn.value)],
      baseOptions,
      allowCustomOptions,
    );
    setOptions(finalOptions);
  }, [dispatch, column, valueList, isReadOnlyOptions, inputColumn.value, allowCustomOptions, subsidiaryOptions]);

  if (!column.options && column.listId) {
    if (!valueList || !valueList.loaded) {
      return renderInputGroup(label, <span>Loading options...</span>);
    }

    if (valueList.error) {
      return renderInputGroup(label, <span>Error: loading options</span>);
    }
  }

  const selectedOptions = options.filter((option) =>
    Array.isArray(inputColumn.value)
      ? inputColumn.value.map((v) => normalizeText(String(v))).includes(normalizeText(String(option.value)))
      : normalizeText(String(inputColumn.value)) === normalizeText(String(option.value)),
  );

  const disabled = isDisabled?.(props);

  return (
    <FormGroup>
      {label}
      <InputGroup className='position-relative w-100 mb-3'>
        {beforeAddon?.element || null}
        <SelectComponent
          placeholder={placeholder}
          isDisabled={disabled}
          options={options}
          setOptions={setOptions}
          value={selectedOptions}
          allowCustomOptions={allowCustomOptions}
          isMulti={isMultipleChoice}
          handleChange={handleChange}
          isReadOnlyOptions={isReadOnlyOptions}
          onSaveCustomOption={
            onSaveCustomOption ? (code: string) => onSaveCustomOption?.(code, column.code) : undefined
          }
        />
        {afterAddon?.element || null}
      </InputGroup>
      {belowInputAddon?.element || null}
    </FormGroup>
  );
};

export default SelectInput;
